import "date-fns";
import React, { ReactNode, useState } from "react";
import Grid from "@mui/material/Grid";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MenuItem, Menu, Divider } from "@mui/material";
import { PickerValue } from "@mui/x-date-pickers/internals";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import 'dayjs/locale/pt-br';
import { DateUtil } from "src/framework/commons/DateUtils";
import dayjs, { Dayjs } from "dayjs";

export interface InputDateTimeProperties {
  id?: string;
  name?: string;
  label: string;
  variant?: "dialog" | "static" | "inline";
  inputVariant?: "filled" | "outlined" | "standard";
  format?: string;
  value?: any;
  defaultValue?: string;
  pattern?: string;
  size?: "small" | "medium";
  fullWidth: boolean;
  error?: boolean;
  readonly?: boolean;
  margin?: "normal" | "dense";
  prefix?: ReactNode;
  onKeyPress?: Function;
  onKeyUp?: Function;
  onKeyDown?: Function;
  onBlur?: Function;
  onFocus?: Function;
  onChange?: (date: string | null) => void,
  disabled?: boolean;
}

const DEFAULT_PARAMENTERS: InputDateTimeProperties = {
  label: "",
  variant: "dialog",
  value: null,
  format: "DD/MM/YYYY HH:mm",
  inputVariant: "outlined",
  size: "small",
  fullWidth: true,
  margin: "normal",
}

interface Position {
  mouseX: any,
  mouseY: any,
}

const initialPosition = {
  mouseX: null,
  mouseY: null,
}

const InputDateTime = (props: InputDateTimeProperties) => {
  const [position, setPosition] = useState<Position>(initialPosition);

  props = { ...DEFAULT_PARAMENTERS, ...props };

  const handleDateChange = (value: PickerValue | Date | Dayjs) => {
    if (!props.onChange) return;

    if (value === null || (dayjs.isDayjs(value) && !value?.isValid())) {
      props.onChange(null);
      return;
    }

    const newDate = new Date(value?.toISOString());
    if (newDate instanceof Date && !isNaN(newDate.getTime())) {
      props.onChange(dayjs.isDayjs(value) ? value?.format('YYYY-MM-DD HH:mm:ss') : value?.toISOString());
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setPosition({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  }

  const handleClose = () => {
    setPosition(initialPosition);
  };

  const handleDate = (option: number) => {
    const data = new Date();
    data.setHours(0);
    data.setMinutes(0);
    data.setSeconds(0);

    switch (option) {
      case 0:
        handleDateChange(data);
        break;
      case 1:
        data.setDate(data.getDate() - 1);
        handleDateChange(data);
        break;
      case 2:
        data.setDate(data.getDate() + 1);
        handleDateChange(data);
        break;
      case 10:
        handleDateChange(DateUtil.firstMonth(DateUtil.decMonth(data)));
        break;
      case 11:
        handleDateChange(DateUtil.lastMonth(DateUtil.decMonth(data)));
        break;
      case 20:
        handleDateChange(DateUtil.firstMonth(data));
        break;
      case 21:
        handleDateChange(DateUtil.lastMonth(data));
        break;
      case 30:
        handleDateChange(DateUtil.firstMonth(DateUtil.incMonth(data)));
        break;
      case 31:
        handleDateChange(DateUtil.lastMonth(DateUtil.incMonth(data)));
        break;
      case 40:
        handleDateChange(new Date(data.getFullYear(), 0, 1));
        break;
      case 41:
        handleDateChange(new Date(data.getFullYear(), 11, 31));
        break;
      case 99:
        handleDateChange(null);
        break;
      default: break;
    }

    setPosition(initialPosition);
  }

  return (
    <LocalizationProvider 
      dateAdapter={AdapterDayjs} 
      adapterLocale="pt-br"
      localeText={{ 
        fieldDayPlaceholder: () => 'DD',
        fieldMonthPlaceholder: () => 'MM',
        fieldYearPlaceholder: () => 'AAAA',
        fieldHoursPlaceholder: () => 'HH',
        fieldMinutesPlaceholder: () => 'MM',

        cancelButtonLabel: 'Cancelar',
        okButtonLabel: 'Confirmar',
        nextStepButtonLabel: 'Próximo',
      }}
    >
      <Grid container justifyContent="space-around">
        <DateTimePicker
          value={props.value ? dayjs(props.value) : null}
          onChange={handleDateChange}
          format={props.format}
          ampm={false}
          slotProps={{
            textField: {
              onContextMenu: handleClick,
              variant: props.inputVariant ?? "outlined",
              fullWidth: props.fullWidth,
              size: props.size,
              margin: "normal",
              label: props.label,
              InputLabelProps: {
                shrink: props.value ? true : undefined,
              },
              InputProps: {
                startAdornment: props.prefix,
              },
            },
          }}
        />
      </Grid>
      <Menu
        keepMounted
        open={position.mouseY !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          position.mouseY !== null && position.mouseX !== null ? { top: position.mouseY, left: position.mouseX } : undefined
        }
      >
        <MenuItem onClick={() => handleDate(0)}>Hoje</MenuItem>
        <MenuItem onClick={() => handleDate(1)}>Ontem</MenuItem>
        <MenuItem onClick={() => handleDate(2)}>Amanhã</MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDate(10)}>Inicio do mês anterior</MenuItem>
        <MenuItem onClick={() => handleDate(11)}>Final do mês anterior</MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDate(20)}>Inicio do mês</MenuItem>
        <MenuItem onClick={() => handleDate(21)}>Final do mês</MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDate(30)}>Inicio do próximo mês</MenuItem>
        <MenuItem onClick={() => handleDate(31)}>Final do próximo mês</MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDate(40)}>Início do ano</MenuItem>
        <MenuItem onClick={() => handleDate(41)}>Final do ano</MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDate(99)}>Limpar</MenuItem>
      </Menu>
    </LocalizationProvider>
  );
};

export { InputDateTime };
